import { useState, useRef, useEffect, useCallback, useContext } from "react";
import { useAuth } from "../../../context/AuthContext";
import { useProfile } from "../../../hooks/useProfile";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../../constant/theme";
import { LanguageContext } from "../../../constant/LanguageContext";
import { translations } from "../constants/translations";
import { useTodayAttendance } from "./useTodayAttendance";

const formatName = (fullName) => {
  if (!fullName) return "";
  return fullName.trim().split(" ").slice(0, 3).join(" ");
};

const useProfileData = () => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const isRTL = language === "ar";
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { profile, profileLoading } = useProfile();

  const t = (key) => translations[key]?.[language] || translations[key]?.en || key;

  const [profilePicture, setProfilePicture] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [alertModal, setAlertModal] = useState({ open: false, title: "", message: "", variant: "info" });
  const [showReminderModal, setShowReminderModal] = useState(true); // Always show for now
  const [employeeInfo, setEmployeeInfo] = useState({
    nameEn: localStorage.getItem("employeeName") || "User",
    nameAr: localStorage.getItem("employeeArabicName") || localStorage.getItem("arabicName") || "المستخدم",
    positionEn: "--",
    positionAr: "--",
  });
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
    isMobile: window.innerWidth <= 768,
    isTablet: window.innerWidth > 768 && window.innerWidth <= 1024,
    isDesktop: window.innerWidth > 1024,
    isLargeDesktop: window.innerWidth > 1440,
  });

  const fileInputRef = useRef(null);
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  // ── Attendance reminder ────────────────────────────────────────────────────
  const infinityCode = profile?.infinityCode || localStorage.getItem("infinityCode");
  const {
    hasTodayCheckIn,
    hasTodayCheckOut,
    checkInTime,
    checkOutTime,
    isLoading: isLoadingAttendance,
  } = useTodayAttendance(infinityCode);

  // ── Show reminder modal on page load ──────────────────────
  useEffect(() => {
    if (!isLoadingAttendance && !hasTodayCheckIn) {
      const hasSeenReminder = sessionStorage.getItem("attendanceReminderShown");
      if (!hasSeenReminder) {
        setShowReminderModal(true);
        sessionStorage.setItem("attendanceReminderShown", "true");
      }
    }
  }, [isLoadingAttendance, hasTodayCheckIn]);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setScreenSize({
        width,
        height: window.innerHeight,
        isMobile: width <= 768,
        isTablet: width > 768 && width <= 1024,
        isDesktop: width > 1024 && width <= 1440,
        isLargeDesktop: width > 1440,
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const showAlert = (title, message, variant = "info") =>
    setAlertModal({ open: true, title, message, variant });

  const closeAlert = () => setAlertModal((prev) => ({ ...prev, open: false }));

  const handleLogout = () => {
    try { logout(); navigate("/login"); }
    catch { navigate("/login"); }
  };

  const loadEmployeeData = useCallback(async () => {
    try {
      if (!profile) return;
      const engName = profile.employeeName || localStorage.getItem("employeeName") || "User";
      const arName = profile.employeeArabicName || localStorage.getItem("employeeArabicName") || "المستخدم";
      setEmployeeInfo({
        nameEn: engName,
        nameAr: arName,
        positionEn: profile.position ?? profile.employeeType ?? "--",
        positionAr: profile.position ?? profile.employeeType ?? "--",
      });
    } catch (error) {
      console.error("Error loading employee data:", error);
    }
  }, [profile]);

  const loadProfilePicture = useCallback(async () => {
    try {
      const infinityCode = localStorage.getItem("infinityCode");
      if (!infinityCode) { setProfilePicture(null); return; }

      const response = await fetch(`${BASE_URL}/employees/pic/${infinityCode}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}`, Accept: "image/*" },
        mode: "cors",
        credentials: "include",
      });

      if (response.ok) {
        const blob = await response.blob();
        setProfilePicture(blob.size > 0 ? URL.createObjectURL(blob) : null);
      } else {
        setProfilePicture(null);
      }
    } catch {
      setProfilePicture(null);
    }
  }, [BASE_URL]);

  const handlePhotoUpload = async (file) => {
    if (!file.type.startsWith("image/")) {
      showAlert("Invalid File", "Please select an image file", "error");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showAlert("File Too Large", "File size must be less than 5MB", "warning");
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("profilePicture", file);

      const response = await fetch(
        `${BASE_URL}/employees/pic/load/${localStorage.getItem("infinityCode")}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          body: formData,
        }
      );

      if (response.ok) {
        await loadProfilePicture();
        showAlert("Success", "Profile picture updated successfully!", "success");
      } else {
        const errorText = await response.text();
        showAlert("Upload Failed", `Upload failed: ${response.status} - ${errorText}`, "error");
      }
    } catch (error) {
      showAlert("Network Error", `Network error: ${error.message}`, "error");
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) handlePhotoUpload(file);
  };

  const triggerFileInput = () => fileInputRef.current?.click();

  useEffect(() => {
    loadEmployeeData();
    loadProfilePicture();
  }, [loadEmployeeData, loadProfilePicture]);

  useEffect(() => {
    return () => {
      if (profilePicture?.startsWith("blob:")) URL.revokeObjectURL(profilePicture);
    };
  }, [profilePicture]);

  // ── Derived employee data ──────────────────────────────────────────────────
  const employeeData = {
    id: profile?.infinityCode || localStorage.getItem("infinityCode") || "--",
    name: formatName(language === "ar" ? employeeInfo.nameAr : employeeInfo.nameEn),
    position: language === "ar" ? employeeInfo.positionAr : employeeInfo.positionEn,
    department: language === "ar" ? employeeInfo.positionAr : employeeInfo.positionEn,
    email: profile?.employeeEmail || localStorage.getItem("email") || "--",
    phone: profile?.employeePhone ? "+20" + profile.employeePhone : "--",
    address: profile?.address || profile?.location || "--",
    joinDate: profile?.hiringDate || null,
    employeeId: profile?.infinityCode || localStorage.getItem("infinityCode") || "--",
    manager: profile?.firstLineManagerName || "--",
    status: profile?.employeeState || "--",
    avatar: (() => {
      const parts = employeeInfo.nameEn.trim().split(" ");
      return (parts[0]?.[0] || "A") + (parts[1]?.[0] || "M");
    })(),
    birthDate: profile?.DoB || null,
    nationalId: profile?.nationalId || "--",
    emergencyContact: profile?.emergencyPhoneNumber ? "+20" + profile.emergencyPhoneNumber : "--",
    workLocation: profile?.location || "--",
    languages: [t("noLanguagesYet")],
    certifications: [t("noCertificationsYet")],
    projects: [
      { name: "E-Commerce Platform", status: "Completed", tech: "React, Node.js" },
      { name: "Mobile Banking App", status: "In Progress", tech: "React Native" },
      { name: "AI Analytics Dashboard", status: "Planning", tech: "Python, TensorFlow" },
    ],
  };

  const performanceData = {
    overallRating: 4.8,
    goals: { completed: 8, inProgress: 3, total: 12 },
    achievements: [
      { title: t("employeeOfMonth"), date: "March 2024" },
      { title: t("innovationAward"), date: "January 2024" },
      { title: t("teamLeadership"), date: "December 2023" },
      { title: t("technicalExcellence"), date: "October 2023" },
    ],
    metrics: { productivity: 92, collaboration: 95, innovation: 88, leadership: 90 },
  };

  const recentActivities = [];

  return {
    // theme / lang
    theme, language, isRTL, t,
    // screen
    screenSize,
    // state
    profilePicture, isUploading,
    activeTab, setActiveTab,
    alertModal, closeAlert,
    showReminderModal, setShowReminderModal,
    // refs
    fileInputRef,
    // handlers
    handleFileSelect, triggerFileInput, handleLogout,
    // data
    employeeData, performanceData, recentActivities,
    // attendance
    hasTodayCheckIn, hasTodayCheckOut, checkInTime, checkOutTime, isLoadingAttendance,
    // profile fetch state
    profileLoading,
  };
};

export default useProfileData;